import Business from '@mui/icons-material/Business';
import { Avatar, Tooltip, Typography, styled } from '@mui/material';
import { ContactOutput } from 'dtos/shared/jobDetails';
import { observer } from 'mobx-react-lite';
import MailOutline from '@mui/icons-material/MailOutline';
import Phone from '@mui/icons-material/Phone';

const LinkText = styled('a')(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
}));

function renderInitials(name: string): string {
  return name.replace(/[^A-Z]/g, '').substring(0, 2);
}

type ContactAvatarProps = {
  title: string;
  contact: ContactOutput;
  tooltip?: boolean;
  [x: string]: any;
};

export const ContactAvatar = observer(({ title, contact, tooltip, ...other }: ContactAvatarProps) => {
  const { sx, ...rest } = other;
  return (
    <Tooltip title={tooltip ? contact.name : undefined}>
      {contact?.photos?.smallPhoto ? (
        <Avatar
          sx={{ width: 32, height: 32, ...sx }}
          alt={contact.name}
          src={`data:image/png;base64, ${contact.photos.smallPhoto}`}
          {...rest}
        />
      ) : (
        <Avatar sx={{ fontSize: 16, width: 32, height: 32, ...sx }} alt={contact.name} {...rest}>
          {renderInitials(contact.name || '')}
        </Avatar>
      )}
    </Tooltip>
  );
});

type ClientAvatarProps = {
  title: string;
  name: string;
  tooltip?: boolean;
  [x: string]: any;
};

export const ClientAvatar = observer(({ title, name, tooltip, ...other }: ClientAvatarProps) => {
  const { sx, ...rest } = other;
  return (
    <Tooltip title={tooltip ? name : undefined}>
      <Avatar sx={{ fontSize: 16, width: 32, height: 32, ...sx }} alt={name} {...rest}>
        <Business fontSize="medium" />
      </Avatar>
    </Tooltip>
  );
});

type ContactProps = {
  title: string;
  contact: ContactOutput;
  [x: string]: any;
};

export const Contact = observer(({ title, contact, ...other }: ContactProps) => {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'start' }} {...other}>
      <ContactAvatar title={title} contact={contact} />
      <div style={{ marginLeft: 8 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          <strong role="term">{contact.name + ', '}</strong>
          <span role="definition">{title}</span>
        </Typography>
        {contact.email && (
          <Typography
            variant="body2"
            sx={{ marginTop: '2px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
          >
            <MailOutline color="primary" fontSize="small" sx={{ marginRight: '5px' }} />
            <LinkText href={`mailto:${contact.email}`}>{contact.email}</LinkText>
          </Typography>
        )}
        {contact.phone && (
          <Typography
            variant="body2"
            sx={{ marginTop: '2px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}
          >
            <Phone color="primary" fontSize="small" sx={{ marginRight: '5px' }} />
            {contact.phone}
          </Typography>
        )}
      </div>
    </div>
  );
});

type CompanyContactProps = {
  title: string;
  name: string;
  [x: string]: any;
};

export const CompanyContact = observer(({ title, name, ...other }: CompanyContactProps) => {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'start' }} {...other}>
      <ClientAvatar title="Client" name={name} />
      <div style={{ marginLeft: 8, marginTop: 8 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          <strong role="term">{name + ', '}</strong>
          <span role="definition">{title}</span>
        </Typography>
      </div>
    </div>
  );
});
